import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="feed-deep-link"
//
// If the current URL points at a feed item anchor that is not in the initial page, force the
// lazy activity-feed frames to load sequentially until the anchor appears, then scroll to it.
export default class extends Controller {
  static values = { maxPages: { type: Number, default: 40 } }

  connect() {
    // Nothing to resolve on ordinary (non-deep-link) visits — skip the global listener entirely.
    if (!this.targetId()) return

    this.loadedFrameIds = new Set()
    this.requestedFrameIds = new Set()
    this.handleFrameLoad = this.handleFrameLoad.bind(this)
    document.addEventListener("turbo:frame-load", this.handleFrameLoad)

    requestAnimationFrame(() => this.findOrLoadTarget())
  }

  disconnect() {
    if (this.handleFrameLoad) document.removeEventListener("turbo:frame-load", this.handleFrameLoad)
  }

  handleFrameLoad(event) {
    const frame = event.target
    if (!this.element.contains(frame)) return

    this.loadedFrameIds.add(frame.id)
    requestAnimationFrame(() => this.findOrLoadTarget())
  }

  findOrLoadTarget() {
    const targetId = this.targetId()
    if (!targetId) return

    const target = document.getElementById(targetId)
    if (target && this.element.contains(target)) {
      target.scrollIntoView({ block: "start" })
      return
    }

    if (this.requestedFrameIds.size >= this.maxPagesValue) return

    const frame = this.nextLazyFrame()
    if (!frame) return

    this.requestedFrameIds.add(frame.id)
    frame.setAttribute("loading", "eager")
  }

  nextLazyFrame() {
    return Array.from(this.element.querySelectorAll("turbo-frame[data-activity-feed-lazy-frame][src]"))
      .find((frame) => !this.requestedFrameIds.has(frame.id) && !this.loadedFrameIds.has(frame.id))
  }

  targetId() {
    if (!window.location.hash) return null

    let id
    try {
      id = decodeURIComponent(window.location.hash.slice(1))
    } catch (err) {
      return null // malformed percent-encoding — treat as an unsupported anchor
    }
    if (!/^(activity|call_log|front_email|mail_slurp_inbound_email|note|bill_note|claim_note|location_note)_\d+$/.test(id)) return null

    return id
  }
}
